import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import { useDraftAction } from "api/useWorkItems"
import { useDraftPackage } from "context/draft-package-provider"
import PackageBoxIcon from "components/PackageBoxIcon"
import type { WorkItem, WorkPackageItem } from "types/work-item"
import styles from "./DraftTray.module.scss"

// A draft's member count, carried on the list payload so the pill/switcher can
// show "· N" without loading each draft's full item list.
const draftCount = (w: WorkItem | undefined): number =>
    ((w?.payload as { draftDetectionIds?: string[] } | undefined)?.draftDetectionIds ?? []).length

const humanizeType = (type: string | null | undefined): string => (type ? type.replace(/_/g, " ") : "Defect")

const itemSubtitle = (item: WorkPackageItem): string => item.defect?.sizeLabel || humanizeType(item.defect?.type)

// How long the pill stays fully expanded with no interaction before folding
// to its icon-only state.
const IDLE_MINIMISE_MS = 5000

const DraftTray = () => {
    const {
        enabled,
        activeDraftId,
        activeDraft,
        drafts,
        setActiveDraft,
        createDraftWith,
        removeFromActiveDraft,
        requestTitleFocus,
        titleFocusNonce,
        batchBarHeight,
        registerTrayPill,
        trayPulseNonce,
    } = useDraftPackage()

    const navigate = useNavigate()
    const location = useLocation()
    const draftAction = useDraftAction(activeDraftId)

    const [expanded, setExpanded] = useState(false)
    const [switcherOpen, setSwitcherOpen] = useState(false)
    const [titleDraft, setTitleDraft] = useState("")
    const titleInputRef = useRef<HTMLInputElement>(null)

    // Present-but-quiet: after a few idle seconds the pill folds to an
    // icon+count badge; hover peeks it back, click expands + opens the peek,
    // and every add re-expands it (so the fly-in has a visible destination).
    const [minimised, setMinimised] = useState(false)
    const [hovered, setHovered] = useState(false)
    const [pulsing, setPulsing] = useState(false)
    const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const summary = drafts.find((d) => d.public_id === activeDraftId)
    const title = activeDraft?.title || summary?.title || "New package"
    const count = activeDraft?.items?.length ?? draftCount(summary)

    const armIdleTimer = () => {
        if (idleTimer.current) clearTimeout(idleTimer.current)
        idleTimer.current = setTimeout(() => setMinimised(true), IDLE_MINIMISE_MS)
    }

    // Idle-minimise runs only while the peek is closed and the cursor is away.
    useEffect(() => {
        if (expanded || hovered) {
            if (idleTimer.current) clearTimeout(idleTimer.current)
            setMinimised(false)
            return
        }
        armIdleTimer()
        return () => {
            if (idleTimer.current) clearTimeout(idleTimer.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, hovered])

    // An arriving add: wake the pill, play the arrival pulse, restart idling.
    useEffect(() => {
        if (trayPulseNonce === 0) return
        setMinimised(false)
        setPulsing(true)
        const t = setTimeout(() => setPulsing(false), 350)
        armIdleTimer()
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trayPulseNonce])

    // Keep the editable title in sync when the active draft (or its title) changes.
    useEffect(() => {
        setTitleDraft(activeDraft?.title || summary?.title || "")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDraftId, activeDraft?.title])

    // "Rename" from a toast/caret expands the tray and focuses the title input.
    useEffect(() => {
        if (titleFocusNonce === 0) return
        setExpanded(true)
        setMinimised(false)
        setSwitcherOpen(false)
        const t = setTimeout(() => {
            titleInputRef.current?.focus()
            titleInputRef.current?.select()
        }, 50)
        return () => clearTimeout(t)
    }, [titleFocusNonce])

    if (!enabled || !activeDraftId) return null
    // The full review page owns this space; don't float the pill over it.
    if (location.pathname.startsWith("/work-items")) return null

    const bottom = batchBarHeight > 0 ? batchBarHeight + 12 : 20

    const commitTitle = () => {
        const next = titleDraft.trim()
        if (!next || !activeDraftId || next === (activeDraft?.title || summary?.title)) return
        draftAction.updateDetails.mutate({ title: next })
    }

    const goToPackage = () => {
        setExpanded(false)
        navigate(`/work-items/${activeDraftId}`)
    }

    const onNewPackage = async () => {
        setSwitcherOpen(false)
        await createDraftWith([])
        requestTitleFocus()
    }

    return (
        <div className={styles.tray} style={{ bottom }}>
            {expanded && (
                <div className={styles.popover}>
                    <div className={styles.head}>
                        <InputText
                            ref={titleInputRef}
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={commitTitle}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur()
                            }}
                            className={styles.titleInput}
                            placeholder="Package title"
                        />
                        {/* Also shown with a single draft: the switcher holds the
                            "New package" row, which must stay reachable at every count
                            (otherwise a second package can never be started here). */}
                        {drafts.length >= 1 && (
                            <Button
                                icon={switcherOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                                className={`p-button-text p-button-sm ${styles.caret}`}
                                onClick={() => setSwitcherOpen((v) => !v)}
                                tooltip="Switch or start a package"
                                aria-label="Switch or start a package"
                            />
                        )}
                    </div>

                    {switcherOpen && (
                        <div className={styles.switcher}>
                            {drafts.map((d) => (
                                <button
                                    key={d.public_id}
                                    className={`${styles.switchRow} ${d.public_id === activeDraftId ? styles.active : ""}`}
                                    onClick={() => {
                                        setActiveDraft(d.public_id)
                                        setSwitcherOpen(false)
                                    }}
                                >
                                    <span className={styles.itemRoad}>{d.title}</span>
                                    <span className={styles.switchCount}>· {draftCount(d)}</span>
                                </button>
                            ))}
                            <button className={`${styles.switchRow} ${styles.newRow}`} onClick={onNewPackage}>
                                + New package
                            </button>
                        </div>
                    )}

                    <div className={styles.items}>
                        {(activeDraft?.items ?? []).length === 0 && <div className={styles.empty}>No defects yet.</div>}
                        {(activeDraft?.items ?? []).map((item) => (
                            <div key={item.subjectId} className={styles.itemRow}>
                                {item.defect?.frameUrl ? (
                                    <img className={styles.thumb} src={item.defect.frameUrl} alt="" />
                                ) : (
                                    <div className={styles.thumbPlaceholder}>
                                        <i className="pi pi-image" />
                                    </div>
                                )}
                                <div className={styles.itemBody}>
                                    <div className={`${styles.itemRoad} ${item.defect ? "" : styles.muted}`}>
                                        {item.defect ? item.defect.roadName || "Unnamed road" : "Defect no longer available"}
                                    </div>
                                    {item.defect && <div className={styles.itemSub}>{itemSubtitle(item)}</div>}
                                </div>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-sm p-button-secondary"
                                    onClick={() => removeFromActiveDraft(item.subjectId)}
                                    tooltip="Remove from package"
                                    aria-label="Remove from package"
                                />
                            </div>
                        ))}
                    </div>

                    <div className={styles.foot}>
                        <Button label="View package" className="p-button-sm p-button-text" onClick={goToPackage} style={{ flex: 1 }} />
                        <Button
                            label="Review & dispatch"
                            icon="pi pi-send"
                            className="p-button-sm"
                            onClick={goToPackage}
                            style={{ flex: 1 }}
                        />
                    </div>
                </div>
            )}

            <button
                ref={registerTrayPill}
                className={`${styles.pill} ${minimised && !expanded ? styles.pillMin : ""} ${pulsing ? styles.pillPulse : ""}`}
                onClick={() => {
                    setMinimised(false)
                    setExpanded((v) => !v)
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                aria-label={minimised ? `${title} · ${count} defects — open draft package` : undefined}
            >
                <span className={styles.glyph}>
                    {/* key remount re-triggers the tilt-and-settle on every add */}
                    <PackageBoxIcon variant="open" size={22} wobble={pulsing} key={trayPulseNonce} />
                </span>
                {minimised && !expanded ? (
                    <span className={styles.badge}>{count}</span>
                ) : (
                    <>
                        <span className={styles.label}>{title}</span>
                        <span className={styles.count}>
                            · {count} defect{count === 1 ? "" : "s"}
                        </span>
                        <i className={`pi ${expanded ? "pi-chevron-down" : "pi-chevron-up"} ${styles.chev}`} />
                    </>
                )}
            </button>
        </div>
    )
}

export default DraftTray
